import axios from 'axios'
import store from '@/store'
import storage from 'store'
import notification from 'ant-design-vue/es/notification'
import message from 'ant-design-vue/es/message'
import { VueAxios } from './axios'
import { ACCESS_TOKEN } from '@/store/mutation-types'
import { tansParams, blobValidate } from "@/utils/ruoyi";
import cache from '@/plugins/cache'
import errorCode from '@/utils/errorCode'
import qs from 'qs'
import { saveAs } from 'file-saver'
import { handleRequestEncryption, handleResponseDecryption } from '@/utils/encrypt-decrypt-handler'
import { stringifySafe, parseSafe, convertBigIntToString } from '@/utils/json-bigint'
import { decrypt, CryptoType } from '@/utils/crypto-util'
import { getEncryptConfig } from '@/utils/encrypt-config'

// 是否显示重新登录
let isReloginShow
axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// 创建 axios 实例
const request = axios.create({
  // API 请求的默认前缀
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 100000, // 请求超时时间
  // 配置响应转换，确保大整数精度不丢失
  transformResponse: [
    function (data) {
      // 如果是字符串，尝试解析为JSON（使用大整数安全解析）
      if (typeof data === 'string') {
        try {
          // 先检查是否是JSON字符串
          data = parseSafe(data)
        } catch (e) {
          // 解析失败，返回原始字符串
        }
      }
      return data
    }
  ]
})

// 异常拦截处理器
const errorHandler = (error) => {
  let { message } = error
  if (message === 'Network Error') {
    message = '后端接口连接异常'
  } else if (message.includes('timeout')) {
    message = '系统接口请求超时'
  } else if (message.includes('Request failed with status code')) {
    message = '系统接口' + message.substr(message.length - 3) + '异常'
  }
  notification.error({
    message: message,
    duration: 5 * 1000
  })
  return Promise.reject(error)
}

// request interceptor
request.interceptors.request.use(config => {
  const token = storage.get(ACCESS_TOKEN)
  // 是否需要防止数据重复提交
  const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
  // 如果 token 存在
  // 让每个请求携带自定义 token 请根据实际情况自行修改
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token // 让每个请求携带自定义token 请根据实际情况自行修改
    // config.headers['accessAccess-Token'] = token
  }
  
  // 加解密处理 - 调用抽取出来的加密处理函数
  config = handleRequestEncryption(config)
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put' || config.method === 'delete')) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime()
    }
    const sessionObj = cache.session.getJSON('sessionObj')
    if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
      cache.session.setJSON('sessionObj', requestObj)
    } else {
      const s_url = sessionObj.url;                  // 请求地址
      const s_data = sessionObj.data;                // 请求数据
      const s_time = sessionObj.time;                // 请求时间
      const interval = 1000;                         // 间隔时间(ms)，小于此时间视为重复提交
      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const message = '数据正在处理，请勿重复提交';
        return Promise.reject(new Error(message))
      } else {
        cache.session.setJSON('sessionObj', requestObj)
      }
    }
  }
  return config
}, errorHandler)

// response interceptor
request.interceptors.response.use((res) => {
  // 如果是 blob 或 arraybuffer 响应，直接返回，不进行任何处理
  if (res.config.responseType === 'blob' || res.config.responseType === 'arraybuffer') {
    return res
  }
  
  // 加解密处理：调用抽取出来的解密处理函数
  let responseData = handleResponseDecryption(res)
  
  const requestUrl = res.config.url
  const isLoginRequest = requestUrl.includes('/login')
  
  // 对于登录接口，直接返回数据，让业务层处理（store中的Login action会检查success字段）
  if (isLoginRequest) {
    return responseData
  }
  
  // 未设置状态码则默认成功状态
  const code = responseData.code || responseData.success !== undefined ? (responseData.success ? '20001' : '500') : '20001'
  // 获取错误信息
  const msg = errorCode[code] || responseData.message || responseData.msg || errorCode['default'] || '请求失败'
  // 二进制数据则直接返回
  if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
    return res.data
  }
  if (code === 401 || code === 'U0231'|| code === 'U0230') {
    if (!isReloginShow) {
      isReloginShow = true
      notification.error({ message: '登录状态已过期，请重新登录' })
      setTimeout(() => {
        store.dispatch('Logout').then(() => {
          isReloginShow = false
          location.href = '/index'
        })
      }, 500)
    }
  } else if (code === 500) {
      notification.error({
        message: msg,
        description: msg
      })
  } else if (code === 601) {
    notification.error({
      message: msg
    })
  } else if(code === "40000" || code === "U0200" || code === "U0210") {
    notification.error({
      message: msg
    })
  } else if (code !== '20001' && code !== 1) {
    notification.error({ message: msg })
    // 传递详细的错误信息，而不是简单的 'error'
    const error = new Error(msg || '请求失败')
    error.response = res
    error.data = responseData
    return Promise.reject(error)
  } else {
    return responseData
  }
}, errorHandler)

const installer = {
  vm: {},
  install(Vue) {
    Vue.use(VueAxios, request)
  }
}

// 通用下载方法
export function download(url, params, filename) {
  const notificationKey = 'download'
  notification.open({
    key: notificationKey,
    message: '正在下载数据，请稍候',
    duration: null,
    icon: h => {
      return h(
        'a-icon',
        {
          props: {
            type: 'loading'
          }
        }
      )
    }
  })
  return request.post(url, params, {
    transformRequest: [(params) => { return qs.stringify(params, { indices: false }) }],
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    responseType: 'blob'
  }).then(async (res) => {
    // res是响应对象，res.data才是blob数据
    const blobData = res.data
    
    // 验证是否为blob格式（通过检查是否为Blob实例）
    if (blobData instanceof Blob) {
      // 检查blob的type，如果是JSON类型，可能是错误响应
      if (blobData.type && blobData.type.includes('application/json')) {
        // 尝试读取JSON内容
        const text = await blobData.text()
        let rspObj
        try {
          rspObj = JSON.parse(text)
        } catch (e) {
          // 解析失败，可能是加密的JSON，尝试解密
          try {
            const decryptConfig = getEncryptConfig()
            if (decryptConfig.enabled) {
              const decryptedText = decrypt(text, decryptConfig.decryptKey, CryptoType.AES)
              rspObj = JSON.parse(decryptedText)
            } else {
              throw new Error('无法解析响应数据')
            }
          } catch (decryptError) {
            message.error('下载文件出现错误，请联系管理员！')
            notification.close(notificationKey)
            return
          }
        }
        // 如果解析成功，说明是错误响应
        const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
        message.error(errMsg)
        notification.close(notificationKey)
        return
      }
      
      // 是有效的blob文件，直接下载
      saveAs(blobData, filename)
      message.success('下载成功')
    } else {
      // 不是blob，尝试作为文本处理
      const isLogin = await blobValidate(blobData)
      if (isLogin) {
        const blob = new Blob([blobData])
        saveAs(blob, filename)
        message.success('下载成功')
      } else {
        const resText = typeof blobData === 'string' ? blobData : await blobData.text()
        let rspObj
        try {
          rspObj = JSON.parse(resText)
        } catch (e) {
          // 解析失败，可能是加密的，尝试解密
          try {
            const decryptConfig = getEncryptConfig()
            if (decryptConfig.enabled) {
              const decryptedText = decrypt(resText, decryptConfig.decryptKey, CryptoType.AES)
              rspObj = JSON.parse(decryptedText)
            } else {
              throw new Error('无法解析响应数据')
            }
          } catch (decryptError) {
            message.error('下载文件出现错误，请联系管理员！')
            notification.close(notificationKey)
            return
          }
        }
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
      message.error(errMsg)
      }
    }
    notification.close(notificationKey)
  }).catch((r) => {
    message.error('下载文件出现错误，请联系管理员！')
    notification.close(notificationKey)
  })
}

export default request

export {
  installer as VueAxios,
  request as axios
}
